/**
 * @module app.um
 */

import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import * as FM from 'app/fm/models/essette-api';
import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, StandardLoadOptions, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { displayComparer } from 'app/shared/lib/utilities';
import { DateTimeValue, DateValue } from 'app/shared/lib/date-time';
import { IAuthSearchFilters, AuthService } from 'app/um/services/auth-service';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { CopyAuth } from 'app/um/components/auth/copy-auth';
import { ExpandEvent } from 'app/shared/elements/ess-table';
import { getReference } from 'app/shared/models/essette-types';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { IAuthContextSettingsDetails } from 'app/um/lib/auth-context';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { DocumentsService } from 'app/core/services/documents-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { guid } from 'app/shared/lib/guid';


@autoinject()
export class ExistingAuths extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull
    public memberPlan: CD.MemberPlan | null = null;

    @bindable.boolean
    public allowNavigation: boolean = true;

    @bindable.boolean
    public minimal: boolean = false;

    @bindable.boolean
    public canGoToAuthPage: boolean = true;

    @bindable.stringNull
    public currentAuthId: string | null = null;

    @bindable.objectNull
    public faxDocument: FM.FaxDocument | null = null;

    protected serviceApprovedCharges: IAuthContextSettingsDetails = { visible: true };
    protected serviceRequestedCharges: IAuthContextSettingsDetails = { visible: true };
    protected servicesStatusReason: IAuthContextSettingsDetails = { visible: true };
    protected servicesDental: IAuthContextSettingsDetails = { visible: true };
    protected servicesDentalStatusReason: IAuthContextSettingsDetails = { visible: true };
    protected drugsAuthDate: IAuthContextSettingsDetails = { visible: true };
    protected drugsExpDate: IAuthContextSettingsDetails = { visible: true };
    protected drugApprovedCharges: IAuthContextSettingsDetails = { visible: true };
    protected drugRequestedCharges: IAuthContextSettingsDetails = { visible: true };
    protected drugRequestedDays: IAuthContextSettingsDetails = { visible: true };
    protected drugsStatusReason: IAuthContextSettingsDetails = { visible: true };

    private searchFilters: IAuthSearchFilters = {};
    private tempSearchFilters: IAuthSearchFilters = {};
    private memberSearchFilters: IMemberSearchFilters = {};

    private authResults: UM.Auth[] = [];

    protected sourceAuth: UM.Auth | null = null;

    // Filter Lookups
    private subClasses: UM.AuthSubClass[] = [];
    private types: UM.AuthType[] = [];
    private providers: CD.Provider[] = [];
    private statuses: UM.AuthStatus[] = [];

    private sorting = new Sorting();

    protected authDetails: Record<string, boolean> = {};

    protected moduleCode: string = this.env.application.code === 'Portal.Providers' ? 'pp' : 'um';

    protected hasCopyAuth: boolean = false;
    protected hasAuthRoute = false;
    protected showAuthSummaryLink: boolean = false;
    protected openAuthSummary = openAuthSummary;

    protected selectedAuthId: string | null = null;

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        addTargetProperty: (document: any /*UM.AuthDocument*/) => { document.auth = { id: this.selectedAuthId } }
    };

    protected id: guid = guid();


    ////****************** Element References ***************************************////
    public $copyModal!: CopyAuth;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly documentService: DocumentsService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);

        // initially filter within a one-year range
        const now = new DateTimeValue(this.env.now()).toParts();
        const lastYear = new DateTimeValue(this.env.now()).toParts();
        lastYear.year = lastYear.year - 1;
        this.searchFilters.requestedDateStart = DateValue.fromParts(lastYear).valueOf();
        this.searchFilters.requestedDateEnd = DateValue.fromParts(now).valueOf();
        this.tempSearchFilters.requestedDateStart = DateValue.fromParts(lastYear).valueOf();
        this.tempSearchFilters.requestedDateEnd = DateValue.fromParts(now).valueOf();
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.documentService.configure(this.documentsConfig);

        this.checkPermissions();
    }

    ////****************** Events ***************************************************////
    protected memberPlanChanged(newMemberPlan: CD.MemberPlan | null, oldMemberPlan: CD.MemberPlan | null): void {
        if(this.faxDocument) this.loadLookups();
        if(newMemberPlan && newMemberPlan.id) {
            this.memberSearchFilters.memberPlan = { id: newMemberPlan.id };
            this.search();
        }
        // If a Non Network Member, clear grid and filters
        else {
            this.clearGrid();
            this.clearFilters();
        }
    }

    /**
     * Handles the grid's sort event
     */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.search();
    }

    protected authDetailLoaded(event: ExpandEvent<UM.Auth>): void {
        const item = event.detail.item;
        const items = event.detail.items;

        if(item && items.includes(item)) {
            this.authDetails[item.id!] = true;
        }
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCopyAuth = this.accountContext.hasPermission('UM.Auth.Insert.CopyAuth');
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess(`${this.moduleCode}.auth.summary`);
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
    }

    protected copyAuth(auth: UM.Auth): void {
        if(auth && auth.id) {
            this.selectedAuthId = auth.id;

            this.sourceAuth = auth;
            this.$copyModal.open();
        }
    }

    protected async openAuth(auth: UM.Auth): Promise<void> {
        if(auth && auth.id) {
            this.selectedAuthId = auth.id;

            // If Document is not null, attach the document to the auth
            if(this.faxDocument?.document) {
                try {
                    if(!(this.documentsConfig && this.documentsConfig.addTargetProperty)) {
                        throw new Error("Documents configuration must define addTargetProperty.")
                    }

                    const result = await confirmation.showCustom(
                        this.i18n.tr('ui:actions.attachTo', { what: this.i18n.tr('core:document.$'), whom: this.i18n.tr('um:auth.$long') }),
                        this.i18n.tr('um:local.createAuth.existingAuthAttachDocumentConfirmation', { what: this.faxDocument.document.$display, whom: auth.number }),
                        confirmation.options.StandardConfirm
                    );

                    // Do not proceed with attachment
                    if(result.dialogResponse !== confirmation.options.OK) { return; }

                    // Attach Document to Auth and Open Auth Page directly to Documents tab
                    const attachDocument: UM.AuthDocument = {
                        id: this.faxDocument.document.id
                    };

                    if(this.documentsConfig && this.documentsConfig.addTargetProperty) {
                        this.documentsConfig.addTargetProperty(attachDocument);
                    }

                    await this.attachDocument(attachDocument);

                    this.router.navigate(`#/um/auth/${auth.id}/documents/?queueTypeCode=FM.Document&queueId=${this.faxDocument?.queue?.id}&queueTargetId=${this.faxDocument?.document?.id}`);
                }
                catch(error) {
                    this.toaster.standard.saved('core:document.$', false);
                    this.logger.error('Error Attaching Document', error);
                }
            }
            else {
                this.router.navigateToRoute('um.auth', { 'authId': auth.id });
            }
        }
    }

    protected submitSearch() {
        this.searchFilters = { ...this.tempSearchFilters };
        this.search();
    }

    public async search(): Promise<void> {

        const options: StandardLoadOptions = {
            all: true,
            sorting: this.sorting,
        };

        try {
            this.state.beginLoad();
            const includes: string[] = [
                'type',
                'class',
                'class.subClass',
                'status',
                'status.reason',
                'assignedTo',
                'provider.servicing.location.provider',
                'services',
                'services.definition.type.bodySiteType'
            ];
            const response = await this.authService.searchAuths(this.searchFilters, this.memberSearchFilters, options, includes);
            if(response) {
                this.authResults = response.results;
                if(this.authResults.length > 0) {
                    this.buildFilters();
                }
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    private async buildFilters(): Promise<void> {
        this.authResults.forEach(auth => {
            if(!auth) return;

            let index = 0;

            // providers are the only options we source locally from the initial data set
            if(auth.provider?.servicing?.location?.provider) {
                let provider = auth.provider.servicing.location.provider;
                index = this.providers.findIndex(p => p.id == provider.id);
                if(index === -1) {
                    this.providers.push(provider);
                }
            }

            // if we have a fax document, we get the subClass, status, and type options from the lookups instead
            if(this.faxDocument) return;

            // subClass
            if(auth.class?.subClass) {
                let subClass = auth.class.subClass;
                index = this.subClasses.findIndex(sc => sc.id == subClass.id);
                if(index === -1) {
                    this.subClasses.push(subClass);
                }
            }

            // type
            if(auth.type) {
                let type = auth.type;
                index = this.types.findIndex(t => t.id == type.id);
                if(index === -1) {
                    this.types.push(type);
                }
            }

            // status
            if(auth.status) {
                let status = auth.status;
                index = this.statuses.findIndex(s => s.id == status.id);
                if(index === -1) {
                    this.statuses.push(status);
                }
            }
        });

        this.subClasses.sort(displayComparer);
        this.types.sort(displayComparer);
        this.providers.sort(displayComparer);
        this.statuses.sort(displayComparer);
    }

    protected clearFilters(performFilter?: boolean): void {
        // when clearing filters, restore the default one-year range
        const now = new DateTimeValue(this.env.now()).toParts();
        const lastYear = new DateTimeValue(this.env.now()).toParts();
        lastYear.year = lastYear.year - 1;
        this.searchFilters.requestedDateStart = DateValue.fromParts(lastYear).valueOf();
        this.searchFilters.requestedDateEnd = DateValue.fromParts(now).valueOf();
        this.searchFilters.type = null;
        this.searchFilters.subClass = null;
        this.searchFilters.providerServicingLocationProvider = null;
        this.searchFilters.status = null;
        this.tempSearchFilters.requestedDateStart = DateValue.fromParts(lastYear).valueOf();
        this.tempSearchFilters.requestedDateEnd = DateValue.fromParts(now).valueOf();
        this.tempSearchFilters.type = null;
        this.tempSearchFilters.subClass = null;
        this.tempSearchFilters.providerServicingLocationProvider = null;
        this.tempSearchFilters.status = null;

        if(performFilter) {
            this.search();
        }
    }

    protected clearGrid(): void {
        this.authResults = [];
        this.subClasses = [];
        this.types = [];
        this.providers = [];
        this.statuses = [];
    }

    protected hasDentalServices(auth: UM.Auth): boolean {
        if(!auth.services || auth.services.length === 0) return false;
        return auth.services?.some(service => service.definition?.type?.bodySiteType?.code === 'Dental');
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async attachDocument(document: UM.AuthDocument): Promise<void> {
        try {
            this.state.beginSave();
            await this.documentService.attachDocument(document);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const [authSubClassesResponse, authTypesResponse, authStatusesResponse] = await Promise.all(
                [
                    this.authService.loadAuthSubClasses({ all: true }),
                    this.authService.loadAuthTypes(true, { all: true }),
                    this.authService.loadAuthStatuses({ all: true })
                ]
            );

            this.subClasses = authSubClassesResponse.results;
            this.types = authTypesResponse.results;
            this.statuses = authStatusesResponse.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
